import styled from '@emotion/styled'
import React from 'react'
import { SimilarPlace } from '../../../hooks/useSimilarPlaces'
import { borderRadius } from '../../../styles/vars'

const Container = styled.div`
  background-color: #fff8e6;
  border: 1px solid #f5c518;
  border-radius: ${borderRadius};
  padding: 12px 16px;
  margin: 12px 0;
`

const Heading = styled.div`
  color: #856404;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
`

const PlaceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: white;
  border: 1px solid #ddd;
  border-radius: ${borderRadius};
  padding: 8px 12px;
  margin-bottom: 8px;
`

const PlaceName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #333;
`

const PlaceAddress = styled.div`
  font-size: 13px;
  color: #666;
`

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #0066cc;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 8px;
  text-decoration: underline;
  white-space: nowrap;

  &:hover {
    color: #004499;
  }
`

const placeAddress = (place: SimilarPlace): string =>
  [place.addressStreetAddress, place.addressCity, place.addressState].filter(Boolean).join(', ')

type Props = {
  places: SimilarPlace[]
  onUsePlace(place: SimilarPlace): void
  onDifferentPlace(): void
  // Ask even with no place to offer (saving was refused but the lookup found nothing or failed).
  alwaysAsk?: boolean
}

// "Is this the same place?" (CVL-163): offers the existing places before a new one is typed or saved.
const SimilarPlacesPrompt: React.FC<Props> = ({ places, onUsePlace, onDifferentPlace, alwaysAsk = false }) => {
  if (places.length === 0 && !alwaysAsk) return null

  return (
    <Container data-testid="similar-places-prompt" role="status">
      <Heading>Is this the same place?</Heading>
      {places.length === 0 && <PlaceAddress>A place with this name is already in the directory in this town or nearby.</PlaceAddress>}
      {places.map(place => (
        <PlaceRow key={place.id}>
          <div>
            <PlaceName>{place.name}</PlaceName>
            <PlaceAddress>{placeAddress(place)}</PlaceAddress>
          </div>
          <LinkButton type="button" onClick={() => onUsePlace(place)}>
            Use this place
          </LinkButton>
        </PlaceRow>
      ))}
      <LinkButton type="button" onClick={onDifferentPlace}>
        No, it&apos;s a different place
      </LinkButton>
    </Container>
  )
}

export default SimilarPlacesPrompt
